import React, { useContext, useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import get from 'lodash/get';
import takeRight from 'lodash/takeRight';
import moment from 'moment';

import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { auth } from '../../config/firebase';
import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { formatCreationDate } from '../data/formatting';
import { DIVISION_BADGES } from '../data/const';
import { resolveIconSelection } from '../data/player_icons';
import {
    chronological,
    getPlacementSummary,
    getDivisionProgress,
    getRecentRatingChange,
    getRecord,
    DIVISION_NAMES,
} from '../data/player_insights';
import PlayerSummaryDialog from '../dialogs/PlayerSummaryDialog';
import { Avatar, RecordText } from '../shared/RivalComponents';
import { RoundWindowSelect, useRoundWindow } from '../shared/RoundWindow';
import { formatToPar } from '../data/scorecard_insights';
import useMyRounds from '../shared/useMyRounds';
import { DS, DeltaPill, Meter, Overline, enterSx, numericSx, trendColor, formatSigned } from '../shared/Dashboard';

const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
    if (values.length < 2) return null;

    const W = 100;
    const H = 40;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const points = values.map((v, i) => [
        (i / (values.length - 1)) * W,
        H - 2 - ((v - min) / span) * (H - 4),
    ]);
    const line = points.map(([x, y]) => `${x},${y}`).join(' ');
    const area = `0,${H} ${line} ${W},${H}`;
    const [endX, endY] = points[points.length - 1];
    const color = trendColor(values[values.length - 1] - values[0]);

    return (
        <Box sx={{ position: 'relative', height: 88 }}>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
                <defs>
                    <linearGradient id="hero-spark-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                        <stop offset="100%" stopColor={color} stopOpacity="0" />
                    </linearGradient>
                </defs>
                <polygon points={area} fill="url(#hero-spark-fill)" />
                <polyline
                    points={line}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                />
            </svg>
            {/* End dot lives outside the stretched SVG so it stays round */}
            <Box sx={{
                position: 'absolute',
                left: `${endX}%`,
                top: `${(endY / H) * 100}%`,
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: color,
                border: `2px solid ${DS.surface}`,
                transform: 'translate(-50%, -50%)',
            }} />
        </Box>
    );
};

const Kpi: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode; delay: number }> = ({ label, value, sub, delay }) => (
    <Grid item xs={6} sm={3} md={2} sx={enterSx(delay)}>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: DS.text, lineHeight: 1.3, mt: 0.25, ...numericSx }}>
            {value}
        </Typography>
        {sub && (
            <Typography sx={{ fontSize: '12px', color: DS.textMuted, mt: 0.25, ...numericSx }}>
                {sub}
            </Typography>
        )}
    </Grid>
);

const PlayerData = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { playerData, summaryData, playerSeasonData, historyData } = context || {};
    const [dialogOpen, setDialogOpen] = useState(false);
    const { roundWindow } = useRoundWindow();

    const createdAt: string | null = get(auth.currentUser, 'metadata.creationTime') ?? null;
    const displayName: string = playerData.playerSettings.displayName;
    // Read from the player's own settings: they update as soon as the icon is changed in the game
    const { icon, iconBorder } = resolveIconSelection(playerData.playerSettings);

    const history = useMemo(() => chronological(historyData), [historyData]);
    const placements = useMemo(() => getPlacementSummary(history), [history]);
    const record = useMemo(() => getRecord(history), [history]);
    const sparkValues = useMemo(() => takeRight(history, roundWindow).map(d => d.currentRating), [history, roundWindow]);
    const recentChange = useMemo(() => getRecentRatingChange(history, roundWindow), [history, roundWindow]);

    const current: number = summaryData.current_rating;
    const peak: number = summaryData.highest_rating;
    const belowPeak = peak - current;
    const division: number = playerSeasonData.division;
    const divisionProgress = getDivisionProgress(division, current);
    const eloPerRound: number = summaryData.mean_points_gained_per_round;
    const { summary: scoring } = useMyRounds();

    return (
        <>
            <Grid item xs={12}>
                <Box sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: DS.radius,
                    border: `1px solid ${DS.border}`,
                    background: `radial-gradient(120% 140% at 100% 0%, ${alpha(DS.brand, 0.14)} 0%, transparent 55%), ${DS.surface}`,
                    ...enterSx(0),
                }}>
                    {/* Identity */}
                    <Box sx={{
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                        <Avatar
                            name={displayName}
                            color={DS.brand}
                            size={56}
                            icon={icon}
                            iconBorder={iconBorder}
                        />

                        <Box sx={{ flex: 1, minWidth: 180 }}>
                            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, color: DS.text }}>
                                {displayName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap', color: DS.textMuted, fontSize: '13px' }}>
                                {division >= 0 && (
                                    <>
                                        <span>{DIVISION_NAMES[division]} Division</span>
                                        <span>·</span>
                                    </>
                                )}
                                <span>Level {playerData.playerStats.playerLevel}</span>
                                {createdAt && (
                                    <>
                                        <span>·</span>
                                        <span>Member since {formatCreationDate(createdAt)}</span>
                                    </>
                                )}
                            </Box>
                        </Box>

                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<OpenInFullIcon sx={{ fontSize: '14px !important' }} />}
                            onClick={() => setDialogOpen(true)}
                            sx={{
                                borderColor: DS.borderStrong,
                                color: DS.text,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '10px',
                                '&:hover': { borderColor: DS.brand, background: alpha(DS.brand, 0.08) },
                            }}
                        >
                            Season summary
                        </Button>
                    </Box>

                    {/* Rating headline + trend */}
                    <Grid container spacing={3} sx={{ px: { xs: 2, sm: 3 }, pt: 3 }} alignItems="flex-end">
                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 2.5 } }}>
                                {division >= 0 && (
                                    <Box
                                        component="img"
                                        src={DIVISION_BADGES[division]}
                                        alt={`${DIVISION_NAMES[division]} Division`}
                                        sx={{
                                            width: { xs: 64, sm: 80 },
                                            height: { xs: 64, sm: 80 },
                                            flexShrink: 0,
                                            filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.45))',
                                        }}
                                    />
                                )}
                                <Box sx={{ minWidth: 0 }}>
                                    <Overline>MP Rating</Overline>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Typography sx={{ fontSize: { xs: '48px', sm: '60px' }, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, color: DS.text, ...numericSx }}>
                                            {current}
                                        </Typography>
                                        <DeltaPill value={recentChange} suffix={`last ${roundWindow}`} />
                                    </Box>
                                    <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 1, ...numericSx }}>
                                        {belowPeak <= 0
                                            ? <>At your season peak of <strong style={{ color: DS.gold }}>{peak}</strong></>
                                            : <>Peak <strong style={{ color: DS.gold }}>{peak}</strong> · {belowPeak} below</>}
                                    </Typography>
                                </Box>
                            </Box>

                            {divisionProgress && (
                                <Box sx={{ mt: 2, maxWidth: 360 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                                            {divisionProgress.nextName} threshold ({divisionProgress.threshold})
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: DS.text, ...numericSx }}>
                                            {divisionProgress.remaining > 0 ? `${divisionProgress.remaining} to go` : 'Reached'}
                                        </Typography>
                                    </Box>
                                    <Meter value={divisionProgress.progress} color={DS.brand} />
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <RoundWindowSelect />
                                {sparkValues.length > 1 && (
                                    <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                                        {sparkValues[0]} → {sparkValues[sparkValues.length - 1]}
                                    </Typography>
                                )}
                            </Box>
                            <Sparkline values={sparkValues} />
                        </Grid>
                    </Grid>

                    {/* KPI strip */}
                    <Box sx={{ mt: 3, borderTop: `1px solid ${DS.border}`, background: 'rgba(0,0,0,0.12)' }}>
                        <Grid container columns={{ xs: 12, md: 14 }} rowSpacing={2.5} columnSpacing={2} sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 3 }}>
                            <Kpi label="Win rate" value={`${placements.winPercent}%`} sub="1st place finishes" delay={0.05} />
                            <Kpi label="W-T-L" value={<RecordText {...record} size={22} />} sub="Season record" delay={0.08} />
                            <Kpi
                                label="Elo / round"
                                value={<span style={{ color: trendColor(eloPerRound) }}>{formatSigned(eloPerRound, 1)}</span>}
                                sub="Season average"
                                delay={0.11}
                            />
                            <Kpi
                                label="Avg to par"
                                value={scoring.averageToPar === null
                                    ? '—'
                                    : <span style={{ color: trendColor(-scoring.averageToPar) }}>{formatToPar(scoring.averageToPar, 1)}</span>}
                                sub="Per round"
                                delay={0.125}
                            />
                            <Kpi label="Rounds" value={summaryData.total_rounds} sub={`${summaryData.rounds_per_day} per day`} delay={0.14} />
                            <Kpi label="Season points" value={summaryData.total_season_points} sub={`${summaryData.season_points_per_round} per round`} delay={0.17} />
                            <Kpi
                                label="Last played"
                                value={summaryData.last_round ? moment(summaryData.last_round).fromNow() : '—'}
                                sub={summaryData.last_round ? moment(summaryData.last_round).format('MMM D, HH:mm') : undefined}
                                delay={0.2}
                            />
                        </Grid>
                    </Box>
                </Box>
            </Grid>

            <PlayerSummaryDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
        </>
    );
};

export default PlayerData;
